import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router';
import { useQuizDetail, useStartAttempt, useSubmitAttempt } from '@/api/quizzes';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { cn } from '@/lib/utils';
import { ArrowLeft, ArrowRight, Send, Clock } from 'lucide-react';
import { QuizIntro } from './quiz-intro';
import { QuizResults } from './quiz-results';
import { QuestionCard } from './question-card';
import { buildSubmitPayload } from './submit-payload';
import type { AnswerValue } from './question-card';
import type { QuizAttempt } from '@/types';

type QuizStage = 'intro' | 'taking' | 'results';

export function QuizAttemptPage() {
  const { quizId: rawId } = useParams<{ quizId: string }>();
  const quizId = Number(rawId) || 0;

  const { data: quiz, isLoading, error } = useQuizDetail(quizId);
  const startAttempt = useStartAttempt(quizId);
  const [attemptId, setAttemptId] = useState(0);
  const submitAttempt = useSubmitAttempt(quizId, attemptId);

  const [stage, setStage] = useState<QuizStage>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, AnswerValue>>({});
  const [result, setResult] = useState<{
    score: number;
    passed: boolean;
    answers: QuizAttempt['answers'];
  } | null>(null);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);

  // Un solo envío en curso: el temporizador (auto-envío al llegar a 0) no puede
  // disparar otro mientras el primero espera respuesta; el segundo pisaba al primero.
  const submittingRef = useRef(false);
  const handleSubmit = useCallback(() => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    submitAttempt.mutate(buildSubmitPayload(answers), {
      onSuccess: (res) => {
        setResult({
          score: res.data.score ?? 0,
          passed: res.data.passed ?? false,
          answers: res.data.answers ?? [],
        });
        setStage('results');
      },
      onError: () => {
        submittingRef.current = false;
      },
    });
  }, [answers, submitAttempt]);

  const handleSubmitRef = useRef<(() => void) | undefined>(undefined);
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  });

  // Countdown timer
  useEffect(() => {
    if (stage !== 'taking' || timeLeft === null) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev === null || prev <= 0 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [stage, timeLeft !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-submit on time expiry
  useEffect(() => {
    if (timeLeft === 0 && stage === 'taking') handleSubmitRef.current?.();
  }, [timeLeft, stage]);

  if (isLoading) return <LoadingSpinner />;
  if (error || !quiz) return <ErrorMessage message="No se pudo cargar la evaluación." />;

  const questions = [...quiz.questions].sort((a, b) => a.sort_order - b.sort_order);
  const totalQuestions = questions.length;

  if (totalQuestions === 0) {
    return (
      <ErrorMessage message="Esta evaluación aún no tiene preguntas configuradas. Contacta a tu administrador." />
    );
  }

  const currentQuestion = questions[currentIndex];

  function handleStart() {
    startAttempt.mutate(undefined, {
      onSuccess: (res) => {
        setAttemptId(res.data.id);
        if (quiz?.time_limit_minutes) setTimeLeft(quiz.time_limit_minutes * 60);
        setStage('taking');
      },
    });
  }

  function selectSingle(qId: number, cId: number) {
    setAnswers((p) => ({ ...p, [qId]: { kind: 'choice', choice_id: cId } }));
  }

  function toggleMulti(qId: number, cId: number) {
    setAnswers((p) => {
      const ex = p[qId];
      const ids = ex?.kind === 'multi' ? [...ex.choice_ids] : [];
      const i = ids.indexOf(cId);
      if (i >= 0) ids.splice(i, 1);
      else ids.push(cId);
      return { ...p, [qId]: { kind: 'multi', choice_ids: ids } };
    });
  }

  function setWritten(qId: number, text: string) {
    setAnswers((p) => ({ ...p, [qId]: { kind: 'written', text } }));
  }

  // --- INTRO ---
  if (stage === 'intro') {
    return (
      <QuizIntro
        quiz={quiz}
        totalQuestions={totalQuestions}
        onStart={handleStart}
        isPending={startAttempt.isPending}
        isError={startAttempt.isError}
      />
    );
  }

  // --- RESULTS ---
  if (stage === 'results' && result) {
    return <QuizResults quiz={quiz} attempt={result} />;
  }

  // --- TAKING ---
  if (!currentQuestion) return <ErrorMessage message="No se encontraron preguntas." />;

  const isLast = currentIndex === totalQuestions - 1;
  const answeredCount = Object.keys(answers).length;
  const timeWarning = timeLeft !== null && timeLeft <= 120 && timeLeft > 0;

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-8">
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span className="font-medium">
          Pregunta {currentIndex + 1} de {totalQuestions}
        </span>
        <div className="flex items-center gap-4">
          {timeLeft !== null && (
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1 font-mono text-sm font-semibold',
                timeLeft < 120
                  ? 'border-red-300 bg-red-50 text-red-600 dark:border-red-700 dark:bg-red-950 dark:text-red-400'
                  : 'border-border bg-background text-foreground',
              )}
            >
              <Clock className="h-4 w-4" />
              {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
            </span>
          )}
          <span>{answeredCount} respondidas</span>
        </div>
      </div>

      {timeWarning && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-700 dark:bg-red-950 dark:text-red-400">
          Quedan menos de 2 minutos. El quiz se enviará automáticamente.
        </div>
      )}

      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
        />
      </div>

      <QuestionCard
        question={currentQuestion}
        answer={answers[currentQuestion.id]}
        onSelectSingle={(cId) => selectSingle(currentQuestion.id, cId)}
        onToggleMulti={(cId) => toggleMulti(currentQuestion.id, cId)}
        onSetWritten={(text) => setWritten(currentQuestion.id, text)}
      />

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCurrentIndex((i) => i - 1)}
          disabled={currentIndex === 0}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
            currentIndex === 0
              ? 'cursor-not-allowed text-muted-foreground opacity-50'
              : 'border border-border bg-background text-foreground hover:bg-accent',
          )}
        >
          <ArrowLeft className="h-4 w-4" /> Anterior
        </button>
        {isLast ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitAttempt.isPending || answeredCount < totalQuestions}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90',
              (submitAttempt.isPending || answeredCount < totalQuestions) &&
                'cursor-not-allowed opacity-60',
            )}
          >
            <Send className="h-4 w-4" />{' '}
            {submitAttempt.isPending ? 'Enviando...' : 'Enviar Respuestas'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setCurrentIndex((i) => i + 1)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Siguiente <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {questions.map((q, idx) => (
          <button
            key={q.id}
            type="button"
            onClick={() => setCurrentIndex(idx)}
            className={cn(
              'h-3 w-3 rounded-full transition-colors',
              idx === currentIndex
                ? 'bg-primary ring-2 ring-primary/30'
                : answers[q.id] !== undefined
                  ? 'bg-primary/50'
                  : 'bg-muted-foreground/30',
            )}
            aria-label={`Pregunta ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
